import { CheckCircle, AlertTriangle, XCircle, Circle, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';

type StatusType = 'ok' | 'warning' | 'critical' | 'pending';
type StatusSize = 'sm' | 'md' | 'lg';

interface StatusIconProps {
  status: StatusType;
  size?: StatusSize;
  /** Show circular background */
  showBackground?: boolean;
  className?: string;
}

interface StatusConfig {
  icon: LucideIcon;
  iconColor: string;
  bgColor: string;
}

const statusConfig: Record<StatusType, StatusConfig> = {
  ok: {
    icon: CheckCircle,
    iconColor: 'text-status-ok',
    bgColor: 'bg-status-ok-bg',
  },
  warning: {
    icon: AlertTriangle,
    iconColor: 'text-status-warning',
    bgColor: 'bg-status-warning-bg',
  },
  critical: {
    icon: XCircle,
    iconColor: 'text-status-critical',
    bgColor: 'bg-status-critical-bg',
  },
  pending: {
    icon: Circle,
    iconColor: 'text-status-pending',
    bgColor: 'bg-status-pending-bg',
  },
};

const sizeConfig: Record<StatusSize, { iconSize: number; containerSize: string }> = {
  sm: { iconSize: 16, containerSize: 'w-7 h-7' },
  md: { iconSize: 20, containerSize: 'w-9 h-9' },
  lg: { iconSize: 24, containerSize: 'w-11 h-11' },
};

export function StatusIcon({
  status,
  size = 'md',
  showBackground = true,
  className,
}: StatusIconProps) {
  const { icon: Icon, iconColor, bgColor } = statusConfig[status];
  const { iconSize, containerSize } = sizeConfig[size];

  if (!showBackground) {
    return <Icon size={iconSize} className={cn(iconColor, className)} />;
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full',
        'transition-colors duration-150',
        containerSize,
        bgColor,
        className
      )}
    >
      <Icon size={iconSize} className={iconColor} />
    </div>
  );
}
